/**
 * Error Recovery Hook
 *
 * Provides error recovery actions with React Router navigation integration.
 * Combines loading context recovery functions with navigation actions
 * for comprehensive error handling in the loading system.
 *
 * @module useErrorRecovery
 */

import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useIntercom } from 'react-use-intercom'

import { setAppIsLoading } from '@/app/slices/appSlice'
import { ACTION_TYPES, getRetryCount, incrementRetryCount, resetRetryCount } from '@/common/utils/ErrorActionMapping'

import { Paths, SUPPORT_EMAIL } from '../utils'

import useLoadingContext from './useLoadingContext'

/**
 * Hook that provides error recovery functions with navigation integration
 *
 * @returns {Object} Recovery functions for different action types
 */
export const useErrorRecovery = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { resetLoading } = useLoadingContext()
    const { hardShutdown } = useIntercom()

    /**
     * Logout and sign out for authentication errors.
     *
     * Navigates to /signout (a public route outside UserRequiredContainer)
     * and lets SignOut.jsx orchestrate the auth-provider logout. Centralising
     * the logout call there avoids the silent-SSO race that comes from
     * calling useAuth.logout() inside an authenticated route subtree
     * (QM-3364).
     */
    const logout = useCallback(() => {
        try {
            console.debug('[Error Recovery] Navigating to /signout for auth-error recovery')
            hardShutdown()
            resetLoading()
            dispatch(setAppIsLoading(false))
            navigate(Paths.SIGNOUT_PATHNAME)
        } catch (error) {
            console.error('[Error Recovery] Navigation to /signout failed:', error)
            // Fallback - direct navigation to signout page
            window.location.href = Paths.SIGNOUT_PATHNAME
        }
    }, [dispatch, navigate, resetLoading, hardShutdown])

    /**
     * Retry by reloading the page - triggers complete reinitialization
     */
    const retry = useCallback(() => {
        try {
            console.debug('[Error Recovery] Incrementing retry counter and reloading page')
            // Increment retry counter before reload (persists across reload)
            incrementRetryCount()
            // Reload page to trigger complete reinitialization
            window.location.reload()
        } catch (error) {
            console.error('[Error Recovery] Page reload failed:', error)
            // Fallback - try to navigate to root
            window.location.href = '/'
        }
    }, [])

    /**
     * Navigate to organization setup (welcome page)
     */
    const setupOrganization = useCallback(() => {
        console.debug('[Error Recovery] Navigating to organization setup')
        // Reset loading state before navigating to prevent loading page from persisting
        resetLoading()
        dispatch(setAppIsLoading(false))
        navigate(Paths.WELCOME_PATHNAME, { replace: true })
    }, [dispatch, navigate, resetLoading])

    /**
     * Show contact support with enhanced error context
     */
    const contactSupport = useCallback((errorObject = null, loadingStep = null, retryCount = 0) => {
        console.debug('[Error Recovery] Contact support action triggered with context:', {
            errorCode: errorObject?.code,
            loadingStep,
            retryCount,
        })

        // Build error context for email
        const errorContext = []

        if (errorObject) {
            errorContext.push(`Error Code: ${errorObject.code || 'Unknown'}`)
            errorContext.push(`Error Message: ${errorObject.message || 'No message available'}`)
            if (errorObject.loadingStep) {
                errorContext.push(`Loading Step: ${errorObject.loadingStep}`)
            }
        }

        if (loadingStep) {
            errorContext.push(`Current Step: ${loadingStep}`)
        }

        errorContext.push(`Retry Count: ${retryCount}`)
        errorContext.push(`Timestamp: ${new Date().toISOString()}`)
        errorContext.push(`User Agent: ${navigator.userAgent}`)
        errorContext.push(`URL: ${window.location.href}`)

        const subject = encodeURIComponent('QuoteMate Application Error - Support Needed')
        const body = encodeURIComponent(
            'Hello Support Team,\n\n' +
                'I encountered an error in the QuoteMate application that requires assistance.\n\n' +
                'Error Details:\n' +
                errorContext.join('\n') +
                '\n\nPlease help resolve this issue.\n\nThank you!'
        )

        const mailtoUrl = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`
        window.open(mailtoUrl, '_blank')
    }, [])

    /**
     * Reset retry counter - call this on successful initialization
     */
    const resetRetryCounter = useCallback(() => {
        console.debug('[Error Recovery] Resetting retry counter')
        resetRetryCount()
    }, [])

    /**
     * Execute recovery action based on action type
     */
    const executeAction = useCallback(
        async (actionType, loadingStep = null, errorObject = null) => {
            console.debug('[Error Recovery] Executing action:', {
                actionType,
                loadingStep,
                errorCode: errorObject?.code,
            })

            switch (actionType) {
                case ACTION_TYPES.RETRY:
                    retry()
                    break

                case ACTION_TYPES.SETUP_ORGANIZATION:
                case ACTION_TYPES.NAVIGATE_TO_WELCOME:
                    setupOrganization()
                    break

                case ACTION_TYPES.CONTACT_SUPPORT: {
                    // Get actual retry count from session storage, not from error object
                    const currentRetryCount = getRetryCount()
                    contactSupport(errorObject, loadingStep, currentRetryCount)
                    break
                }

                case ACTION_TYPES.LOGOUT:
                    await logout()
                    break

                default:
                    console.warn('[Error Recovery] Unknown action type:', actionType)
                    break
            }
        },
        [retry, setupOrganization, contactSupport, logout]
    )

    return {
        // Individual action functions
        retry,
        logout,
        setupOrganization,
        contactSupport,
        resetRetryCounter,

        // Generic action executor
        executeAction,
    }
}
